import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  OnInit,
  output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { EditorModule } from 'primeng/editor';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { ToastrService } from 'ngx-toastr';
import { finalize, merge } from 'rxjs';
import { CommunicationType, getCommunicationTypeLabel, ReadFileResponse } from '../../core/models';
import { CommunicationTemplateService } from '../../services/communication-template.service';
import { CommunicationDTO, CommunicationService } from '../../services/communication.service';
import { PERMISSION_UI_DIRECTIVES } from '../../directives/permission.directive';
import { apiFailureMessage, httpErrorApiMessage } from '../../core/api-response-utils';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const htmlNotBlankValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = (control.value ?? '').toString();
  const text = value.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim();
  return text ? null : { required: true };
};

interface ComposeAttachment {
  key: string;
  name: string;
  contentType: string;
  size: number;
  fromTemplate: boolean;
  fileId?: number;
  file?: File;
}

@Component({
  selector: 'app-send-communication-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    SelectModule,
    InputTextModule,
    AutoCompleteModule,
    EditorModule,
    TooltipModule,
    PERMISSION_UI_DIRECTIVES
  ],
  templateUrl: './send-communication-form.component.html',
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        min-height: 0;
        height: 100%;
      }

      .compose-row {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        min-height: 2.125rem;
        padding: 0.25rem 0.75rem;
        border-bottom: 1px solid rgb(241 245 249);
      }

      .compose-label {
        flex-shrink: 0;
        width: 4.5rem;
        font-size: 0.75rem;
        font-weight: 500;
        color: rgb(100 116 139);
        user-select: none;
      }

      .compose-field {
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }

      .compose-error {
        font-size: 0.6875rem;
        color: rgb(220 38 38);
        line-height: 1.2;
      }

      :host ::ng-deep .compose-input,
      :host ::ng-deep .compose-input.p-inputtext {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        padding: 0.125rem 0 !important;
        font-size: 0.875rem;
        line-height: 1.4;
        color: rgb(15 23 42);
        border-radius: 0;
      }

      :host ::ng-deep .compose-input::placeholder {
        color: rgb(203 213 225);
      }

      :host ::ng-deep .compose-input:focus {
        outline: none;
        box-shadow: none !important;
      }

      :host ::ng-deep .compose-select.p-select {
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
      }

      :host ::ng-deep .compose-select .p-select-label {
        padding: 0.125rem 0 !important;
        font-size: 0.875rem;
        color: rgb(15 23 42);
      }

      :host ::ng-deep .compose-select .p-select-label.p-placeholder {
        color: rgb(203 213 225);
      }

      :host ::ng-deep .compose-select .p-select-dropdown {
        width: 1.5rem;
        color: rgb(148 163 184);
      }

      :host ::ng-deep .send-communication-cc .p-autocomplete,
      :host ::ng-deep .send-communication-cc .p-autocomplete-fluid {
        width: 100%;
        min-width: 0;
        display: flex;
      }

      :host ::ng-deep .send-communication-cc .p-autocomplete-input-multiple {
        width: 100%;
        min-width: 0;
        min-height: 0;
        height: auto;
        padding: 0 !important;
        border: none !important;
        box-shadow: none !important;
        background: transparent !important;
        flex-wrap: wrap;
        align-items: center;
        gap: 0.25rem;
      }

      :host ::ng-deep .send-communication-cc .p-autocomplete-chip.p-chip {
        max-width: 100%;
        min-width: 0;
        height: 1.375rem;
        font-size: 0.75rem;
        background: rgb(241 245 249);
        border: none;
        color: rgb(51 65 85);
      }

      :host ::ng-deep .send-communication-cc .p-autocomplete-chip .p-chip-label {
        min-width: 0;
        flex: 1 1 auto;
        white-space: normal;
        overflow-wrap: anywhere;
        word-break: break-all;
      }

      :host ::ng-deep .send-communication-cc .p-autocomplete-input-chip {
        flex: 1 1 6rem;
        min-width: 6rem;
        max-width: 100%;
      }

      :host ::ng-deep .send-communication-cc .p-autocomplete-input-chip input {
        padding: 0.125rem 0 !important;
        font-size: 0.875rem;
      }

      :host ::ng-deep .send-communication-message-editor {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        border-top: 1px solid rgb(241 245 249);
      }

      :host ::ng-deep .send-communication-message-editor .compose-editor.p-editor {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        height: 100%;
        border: none;
        border-radius: 0;
        box-shadow: none;
        overflow: hidden;
      }

      :host ::ng-deep .send-communication-message-editor .p-editor-toolbar {
        flex-shrink: 0;
        background: transparent;
        border: none;
        border-bottom: 1px solid rgb(241 245 249);
        border-radius: 0;
        padding: 0.25rem 0.5rem;
        flex-wrap: wrap;
        opacity: 0.7;
        transition: opacity 0.15s;
      }

      :host ::ng-deep .send-communication-message-editor:hover .p-editor-toolbar,
      :host ::ng-deep .send-communication-message-editor:focus-within .p-editor-toolbar {
        opacity: 1;
      }

      :host ::ng-deep .send-communication-message-editor .p-editor-toolbar .ql-picker-options {
        z-index: 1100;
      }

      :host ::ng-deep .send-communication-message-editor .p-editor-content {
        display: flex !important;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;
      }

      :host ::ng-deep .send-communication-message-editor .ql-container {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
        height: 100%;
        border: none;
        font-size: 0.875rem;
      }

      :host ::ng-deep .send-communication-message-editor .ql-editor {
        flex: 1 1 auto;
        min-height: 0;
        height: 100%;
        overflow-y: auto;
        padding: 0.75rem;
        font-size: 0.875rem;
        line-height: 1.6;
        white-space: pre-wrap;
        word-wrap: break-word;
        overflow-wrap: anywhere;
      }

      :host ::ng-deep .send-communication-message-editor .ql-editor :is(p, li, ol, ul, span, div) {
        font-size: 0.875rem;
        line-height: 1.6;
      }

      .compose-attachments {
        align-items: flex-start;
        min-height: auto;
        padding-top: 0.5rem;
        padding-bottom: 0.5rem;
      }

      .compose-attachment-chip {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        max-width: 100%;
        height: 1.5rem;
        padding: 0 0.25rem 0 0.5rem;
        border-radius: 9999px;
        background: rgb(241 245 249);
        color: rgb(51 65 85);
        font-size: 0.75rem;
        line-height: 1;
      }

      .compose-attachment-chip--extra {
        background: rgb(238 242 255);
        color: rgb(67 56 202);
      }

      .compose-attachment-remove {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.125rem;
        height: 1.125rem;
        border: none;
        border-radius: 9999px;
        background: transparent;
        color: inherit;
        cursor: pointer;
      }

      .compose-attachment-remove:hover {
        background: rgb(226 232 240);
      }

      .compose-attachment-add {
        display: inline-flex;
        align-items: center;
        gap: 0.25rem;
        height: 1.5rem;
        padding: 0 0.5rem;
        border: 1px dashed rgb(203 213 225);
        border-radius: 9999px;
        background: transparent;
        color: rgb(100 116 139);
        font-size: 0.75rem;
        cursor: pointer;
      }

      .compose-attachment-add:hover {
        border-color: rgb(165 180 252);
        color: rgb(79 70 229);
        background: rgb(238 242 255);
      }
    `
  ]
})
export class SendCommunicationFormComponent implements OnInit {
  saleId = input<number | null>(null);
  subscriptionId = input<number | null>(null);
  chargeId = input.required<number>();
  defaultEmail = input('');
  templateType = input<CommunicationType | null>(null);
  /** Si es false, el botón Enviar debe renderizarse en el contenedor padre. */
  showFooterActions = input(true);

  sent = output<CommunicationDTO>();
  formStateChange = output<void>();

  private readonly communicationService = inject(CommunicationService);
  private readonly templateService = inject(CommunicationTemplateService);
  private readonly fb = inject(FormBuilder);
  private readonly toastr = inject(ToastrService);
  private readonly cdr = inject(ChangeDetectorRef);

  sending = false;
  previewLoading = false;
  templates: { label: string; value: number }[] = [];
  readonly messageEditorStyle = { height: '100%' };
  templatesLoading = false;
  templatesLoaded = false;
  attachmentsLoading = false;
  composeAttachments: ComposeAttachment[] = [];
  private extraAttachmentSeq = 0;

  sendForm = this.fb.group({
    templateId: [null as number | null, Validators.required],
    toEmail: ['', [Validators.required, Validators.email]],
    subject: ['', Validators.required],
    messageBody: ['', [Validators.required, htmlNotBlankValidator]],
    ccEmails: [[] as string[]]
  });

  constructor() {
    effect(() => {
      const email = (this.defaultEmail() ?? '').trim();
      if (email && !this.sendForm.controls.toEmail.dirty) {
        this.sendForm.patchValue({ toEmail: email });
      }
    });
  }

  ngOnInit(): void {
    this.initializeForm();
    merge(this.sendForm.statusChanges, this.sendForm.valueChanges).subscribe(() => {
      this.formStateChange.emit();
      this.cdr.markForCheck();
    });
  }

  isSubmitDisabled(): boolean {
    return this.sending || this.sendForm.invalid || this.attachmentsLoading;
  }

  resetForm(): void {
    this.initializeForm();
  }

  onCcAdd(event: { value: string }): void {
    this.sanitizeCcEmails(event.value);
  }

  onTemplateChange(): void {
    this.sendForm.patchValue({ subject: '', messageBody: '' });
    this.composeAttachments = [];
    const templateId = this.sendForm.controls.templateId.value;
    if (templateId == null) {
      return;
    }
    this.previewLoading = true;
    this.loadTemplateAttachments(templateId);
    this.templateService.preview({
      templateId,
      chargeId: this.chargeId()
    }).pipe(
      finalize(() => {
        this.previewLoading = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (previewResponse) => {
        if (previewResponse.ok && previewResponse.body) {
          this.sendForm.patchValue({
            subject: previewResponse.body.subject ?? '',
            messageBody: previewResponse.body.body ?? ''
          });
        }
      },
      error: () => this.toastr.error('No se pudo generar la vista previa')
    });
  }

  sendCommunication(): void {
    this.sanitizeCcEmails();
    if (this.sendForm.invalid || this.attachmentsLoading) {
      this.sendForm.markAllAsTouched();
      return;
    }
    const { templateId, toEmail, subject, messageBody, ccEmails } = this.sendForm.getRawValue();
    if (templateId == null || !toEmail?.trim() || !subject?.trim() || !messageBody?.trim()) {
      return;
    }
    this.sending = true;
    this.formStateChange.emit();
    void this.buildAndSend({
      templateId,
      toEmail: toEmail.trim(),
      subject: subject.trim(),
      messageBody: messageBody.trim().replace(/<p([^>]*)>(\s*<br\s*\/?>\s*|\s*)<\/p>/gi, '<p$1>&nbsp;</p>'),
      ccEmails: (ccEmails ?? []).length > 0 ? ccEmails : undefined,
      chargeId: this.chargeId()
    });
  }

  private async buildAndSend(payload: Parameters<CommunicationService['send']>[0]): Promise<void> {
    try {
      const extra = this.composeAttachments.filter((item) => !item.fromTemplate && item.file);
      payload.attachmentFileIds = this.composeAttachments
        .filter((item) => item.fromTemplate && item.fileId != null)
        .map((item) => item.fileId!);
      if (extra.length > 0) {
        payload.attachments = await Promise.all(extra.map(async (item) => ({
          name: item.name,
          contentType: item.contentType,
          contentBase64: await this.readFileAsBase64(item.file!)
        })));
      }
      const saleId = this.saleId();
      const subscriptionId = this.subscriptionId();
      if (saleId != null) {
        payload.saleId = saleId;
      }
      if (subscriptionId != null) {
        payload.subscriptionId = subscriptionId;
      }
      this.communicationService.send(payload).pipe(
        finalize(() => {
          this.sending = false;
          this.cdr.markForCheck();
        })
      ).subscribe({
        next: (response) => {
          if (response.ok && response.body) {
            const status = response.body.status;
            if (status === 'SENT') {
              this.toastr.success('Comunicación enviada correctamente');
            } else if (status === 'FAILED') {
              this.toastr.error(response.body.errorMessage ?? 'El envío falló');
            } else {
              this.toastr.info('Comunicación registrada');
            }
            this.sent.emit(response.body);
            this.initializeForm();
          } else {
            this.toastr.error(apiFailureMessage(response, 'No se pudo enviar la comunicación'));
          }
        },
        error: (err) => this.toastr.error(httpErrorApiMessage(err, 'Error al enviar la comunicación'))
      });
    } catch {
      this.sending = false;
      this.toastr.error('No se pudieron leer los adjuntos');
      this.cdr.markForCheck();
    }
  }

  private initializeForm(): void {
    const toEmail = (this.defaultEmail() ?? '').trim();
    this.sendForm.reset({
      templateId: null,
      toEmail,
      subject: '',
      messageBody: '',
      ccEmails: []
    });
    this.sendForm.markAsPristine();
    this.previewLoading = false;
    this.composeAttachments = [];
    this.attachmentsLoading = false;
    this.loadTemplates();
  }

  onExtraAttachmentSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';
    const MAX_SIZE_MB = 10;
    const maxBytes = MAX_SIZE_MB * 1024 * 1024;
    for (const file of files) {
      if (file.size > maxBytes) {
        this.toastr.error(`${file.name} supera ${MAX_SIZE_MB} MB`);
        continue;
      }
      this.extraAttachmentSeq += 1;
      this.composeAttachments = [
        ...this.composeAttachments,
        {
          key: `extra-${this.extraAttachmentSeq}`,
          name: file.name,
          contentType: file.type || 'application/octet-stream',
          size: file.size,
          fromTemplate: false,
          file
        }
      ];
    }
    this.cdr.markForCheck();
  }

  removeComposeAttachment(key: string): void {
    this.composeAttachments = this.composeAttachments.filter((item) => item.key !== key);
    this.cdr.markForCheck();
  }

  attachmentTooltip(file: ComposeAttachment): string {
    const origin = file.fromTemplate ? 'De la plantilla' : 'Nuevo (solo este envío)';
    return `${file.name} · ${this.formatFileSize(file.size)} · ${origin}`;
  }

  formatFileSize(size?: number): string {
    if (size == null || !Number.isFinite(size)) {
      return '-';
    }
    if (size < 1024) {
      return `${size} B`;
    }
    if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  }

  private loadTemplateAttachments(templateId: number): void {
    this.attachmentsLoading = true;
    this.templateService.listAttachments(templateId).pipe(
      finalize(() => {
        this.attachmentsLoading = false;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        const extras = this.composeAttachments.filter((item) => !item.fromTemplate);
        const fromTemplate = (response.body ?? []).map((file: ReadFileResponse) => ({
          key: `template-${file.id}`,
          name: file.name ?? 'archivo',
          contentType: file.contentType ?? 'application/octet-stream',
          size: file.size ?? 0,
          fromTemplate: true,
          fileId: file.id
        }));
        this.composeAttachments = [...fromTemplate, ...extras];
      },
      error: () => this.toastr.error('No se pudieron cargar los adjuntos de la plantilla')
    });
  }

  private readFileAsBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.includes(',') ? result.split(',')[1] : result;
        resolve(base64 ?? '');
      };
      reader.onerror = () => reject(reader.error ?? new Error('No se pudo leer el archivo'));
      reader.readAsDataURL(file);
    });
  }

  private sanitizeCcEmails(justAdded?: string): void {
    const toEmail = (this.sendForm.controls.toEmail.value ?? '').trim().toLowerCase();
    const raw = [...(this.sendForm.controls.ccEmails.value ?? [])];
    const result: string[] = [];
    const seen = new Set<string>();
    const addedNorm = justAdded?.trim().toLowerCase();

    for (const item of raw) {
      const email = item.trim();
      if (!email) {
        continue;
      }
      if (!EMAIL_PATTERN.test(email)) {
        if (email.toLowerCase() === addedNorm) {
          this.toastr.warning('Ingresa un correo electrónico válido');
        }
        continue;
      }
      const norm = email.toLowerCase();
      if (norm === toEmail) {
        if (norm === addedNorm) {
          this.toastr.warning('Ese correo ya está en Para');
        }
        continue;
      }
      if (seen.has(norm)) {
        if (norm === addedNorm) {
          this.toastr.info('Ese correo ya está en copia');
        }
        continue;
      }
      seen.add(norm);
      result.push(email);
    }

    if (result.length !== raw.length) {
      this.sendForm.patchValue({ ccEmails: result });
      this.cdr.markForCheck();
    }
  }

  private loadTemplates(): void {
    if (this.templatesLoaded || this.templatesLoading) {
      return;
    }
    this.templatesLoading = true;
    const filters: Record<string, unknown> = { state: 'ACTIVE' };
    const type = this.templateType();
    if (type) {
      filters['type'] = type;
    }
    this.templateService.list(filters, { page: 0, size: 200, sort: ['name,asc'] }).pipe(
      finalize(() => {
        this.templatesLoading = false;
        this.templatesLoaded = true;
        this.cdr.markForCheck();
      })
    ).subscribe({
      next: (response) => {
        if (response.ok) {
          this.templates = (response.body ?? []).map((t) => ({
            label: `${t.name ?? 'Plantilla'} (${getCommunicationTypeLabel(t.type)})`,
            value: t.id!
          }));
        }
      },
      error: () => this.toastr.error('No se pudieron cargar las plantillas')
    });
  }
}
